class DetailsDisclosure extends HTMLElement {
  constructor(focusOut = true) {
    super();
    this.header = document.getElementById('shopify-section-header');
    this.name = this.dataset.name ?? 'disclosure';
    this.toggleColor = this.dataset.toggleColor ?? null;
    this.containerClasses = [`has-open-${this.name}`];
    this.focusOut = focusOut;
    this.onSummaryClick = this.handleSummaryClick.bind(this);

    if (this.toggleColor) {
      this.containerClasses.push(`color-${this.toggleColor}`);
    }

    this.disclosureContainer = this.closest('[data-disclosure-container]');
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.querySelector('summary');

    this.bindEvents();
  }

  bindEvents() {
    this.summaryElement.addEventListener('click', this.onSummaryClick);

    if (this.focusOut) {
      this.detailsElement.addEventListener('focusout', this.onFocusOut.bind(this));
    }

    this.detailsElement.addEventListener(
      'keyup',
      (event) => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
  }

  isOpen() {
    return this.detailsElement.hasAttribute('open');
  }

  handleSummaryClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  open() {
    this.detailsElement.setAttribute('open', true);
    this.disclosureContainer.classList.add(...this.containerClasses);
  }

  close() {
    this.detailsElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', false);
    this.disclosureContainer.classList.remove(...this.containerClasses);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class MenuDisclosure extends DetailsDisclosure {
  constructor() {
    super();

    this.item = this.closest('li');
    this.child = this.querySelector('[data-disclosure-child]');
    this.siblings = Array.from(this.closest('[data-disclosure-wrapper]').querySelectorAll('menu-disclosure'));
  }

  bindEvents() {
    super.bindEvents();
    this.rootLinks = this.closest('[data-disclosure-wrapper]').querySelectorAll('.header__menu-item--link');
    this.summaryElement.removeEventListener('click', this.onSummaryClick);
    this.summaryElement.addEventListener('click', e => e.preventDefault());
    this.addEventListener('mouseenter', this.onMouseOver.bind(this));
    this.header.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.rootLinks.forEach(link => link.addEventListener('mouseenter', this.onRootLinkHover.bind(this)));
  }

  isOpen() {
    return this.item.classList.contains('is-open');
  }

  onMouseOver() {
    this.open();
  }

  onMouseLeave() {
    if (typeof this.closingTime !== 'undefined') return;

    this.closingTime = setTimeout(() => {
      this.close();
    }, 150);
  }

  onRootLinkHover() {
    if (this.isOpen()) {
      this.close();
    }
  }
  
  open() {
    this.cancelClose();
    this.closeSiblings();

    if (this.isOpen()) return;

    super.open();
    this.item.classList.add('is-open');

    setTimeout(() => {
      this.disclosureContainer.classList.add('has-menu-buffer');
      this.item.classList.remove('is-instant');
      this.siblings.forEach(sibling =>
        sibling !== this && sibling.item.classList.add('is-instant')
      );
    });
  }

  hasOpenSibling() {
    return this.siblings.some(sibling => sibling.isOpen());
  }

  cancelClose() {
    clearTimeout(this.closingTime);
    this.closingTime = undefined;
  }

  closeSiblings() {
    this.siblings.forEach(sibling => sibling !== this && sibling.close());
  }

  close() {
    this.cancelClose();

    if (!this.isOpen()) return;
    
    this.item.classList.remove('is-open');
    this.summaryElement.setAttribute('aria-expanded', false);

    if (!this.hasOpenSibling()) {
      this.disclosureContainer.classList.remove(...this.containerClasses);
    }

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = time => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else if (!this.isOpen()) {
        this.detailsElement.removeAttribute('open');
        
        if (!this.hasOpenSibling()) {
          this.disclosureContainer.classList.remove('has-menu-buffer');
          this.siblings.forEach(sibling => {
            sibling.item.classList.remove('is-instant');
          });
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-disclosure', MenuDisclosure);

class FormDisclosure extends DetailsDisclosure {
  constructor() {
    super(false);
    this.name = this.dataset.name ?? 'form';
  }

  onBodyClick(event) {
    if (!this.contains(event.target)) this.close(false);
  }

  open(event) {
    super.open(event);

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    document.body.addEventListener('click', this.onBodyClickEvent);

    trapFocus(
      this.detailsElement.querySelector('[tabindex="-1"]'),
      this.detailsElement.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    super.close();
    removeTrapFocus(focusToggle ? this.summaryElement : null);
    document.body.removeEventListener('click', this.onBodyClickEvent);
  }
}

customElements.define('form-disclosure', FormDisclosure);

class SearchDisclosure extends FormDisclosure {
  constructor() {
    super();
    this.searchInput = this.querySelector('input[type="search"]');
    this.submitButton = this.querySelector('[data-submit]');
    this.bindSearchEvents();
  }

  getQuery() {
    return this.searchInput.value.trim();
  }

  bindSearchEvents() {
    if (this.submitButton) {
      this.submitButton.addEventListener('click', (event) => {
        if (this.isOpen() && !this.getQuery().length) {
          this.close(false);
        }
      });
    }
  }
}

customElements.define('search-disclosure', SearchDisclosure);
